import React from 'react';
import {
  Box,
  Paper,
  IconButton,
  Collapse,
  Typography
} from '@mui/material';
import { ExpandMore, ExpandLess, DragIndicator } from '@mui/icons-material';
import Draggable from 'react-draggable';
import { Trans } from '@lingui/react';
import { CustomUnitFields, UnitSystem } from 'providers/api';
import { StopeDimensions } from './stopeFillSceneState';
import StopeVolumeTable, { PourVolumeData } from './StopeVolumeTable';

interface StopeVolumePanelProps {
  totalVolume: number;
  remainingVolume: number;
  pours: PourVolumeData[];
  dimensions?: StopeDimensions;
  drawerOpen?: boolean;
  unitSystemPreference?: UnitSystem;
  customUnitFields?: CustomUnitFields[];
}

const StopeVolumePanel = ({
  totalVolume, remainingVolume, pours, dimensions, drawerOpen = false, unitSystemPreference, customUnitFields,
}: StopeVolumePanelProps) => {
  const [expanded, setExpanded] = React.useState(true);
  const nodeRef = React.useRef<HTMLDivElement>(null);

  // Default position accounts for sidebar (400px when open)
  const defaultX = drawerOpen ? -420 : -10;

  return (
    <Draggable
      nodeRef={nodeRef}
      defaultPosition={{ x: defaultX, y: 10 }}
      handle=".volume-panel-handle"
    >
      <Paper
        ref={nodeRef}
        elevation={3}
        sx={{
          position: 'absolute',
          // The stage reserves the ribbon toolbar with padding-top, but an absolutely positioned
          // child measures `top` from the padding box, so that padding does not move it. Without
          // this the header bar - drag handle, title, collapse arrow - sits behind the ribbon.
          top: 'var(--stopes-toolbar-height, 0px)',
          right: 0,
          // A fixed width (not min/max) so the table inside can lay out against a known size and
          // never needs a horizontal scrollbar.
          width: 340,
          bgcolor: 'background.paper',
          opacity: 0.95,
          zIndex: 1000,
          overflow: 'hidden',
        }}
      >
        <Box
          className="volume-panel-handle"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 1,
            py: 0.5,
            cursor: 'move',
            bgcolor: 'action.hover',
            borderBottom: expanded ? 1 : 0,
            borderColor: 'divider',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <DragIndicator fontSize="small" color="action" />
            <Typography variant="caption" color="text.secondary">
              <Trans id="Fill Summary">Fill Summary</Trans>
            </Typography>
          </Box>
          <IconButton size="small" onClick={() => setExpanded(!expanded)}>
            {expanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
          </IconButton>
        </Box>

        <Collapse in={expanded}>
          {/* One row per pour, with nothing to stop it: a long fill sequence otherwise grows the
              panel past the bottom of the viewport, putting the Remaining and Filled rows beneath
              it out of reach. The chart panel never hits this because its height is fixed. */}
          <Box sx={{ maxHeight: '60vh', overflowY: 'auto', overflowX: 'hidden' }}>
            <StopeVolumeTable
              totalVolume={totalVolume}
              remainingVolume={remainingVolume}
              pours={pours}
              dimensions={dimensions}
              unitSystemPreference={unitSystemPreference}
              customUnitFields={customUnitFields}
            />
          </Box>
        </Collapse>
      </Paper>
    </Draggable>
  );
};

export default StopeVolumePanel;
